import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import { DateRange } from "@mui/x-date-pickers-pro";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation } from "react-query";

import AppButton from "../components/AppButton";
import Field from "../components/Field";
import CustomDateRangePicker from "../components/Inputs/DatePickers/CustomDateRangePicker";
import ResponsiveDialog from "../components/ResponsiveDialog";
import { SX_FORM_CANCEL, SX_FORM_SUBMIT } from "../Configuration/styles";
import ImpactSummary from "./ImpactSummary";
import { createApplication, fetchImpact } from "./api";
import { ExcludedPeriod, ImpactReport, PricingTierGrid } from "./types";

interface Props {
  grid: PricingTierGrid;
  onClose: () => void;
}

const DETAIL_PAGE_SIZE = 20;

const formatPeriod = (period: ExcludedPeriod, fallback: string) =>
  [period.date_from ?? fallback, period.date_to ?? fallback].join(" → ");

/**
 * "Show me what this does, then do it."
 *
 * The apply button stays out of reach until a dry-run has been run for exactly
 * the options on screen: `checked` is stamped with the signature of the options
 * it was computed for, and any change drops it. Writing several hundred prices
 * is not something to reach by accident.
 */
const ApplyDialog = ({ grid, onClose }: Props) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [excluded, setExcluded] = useState<ExcludedPeriod[]>([]);
  const [draftPeriod, setDraftPeriod] = useState<DateRange<DateTime<boolean>>>([
    null,
    null,
  ]);
  const [includeLocked, setIncludeLocked] = useState(false);
  const [offset, setOffset] = useState(0);
  const [checked, setChecked] = useState<{
    signature: string;
    report: ImpactReport;
  } | null>(null);

  // Only what changes the prices that would be written — paging the detailed
  // list must not invalidate the check the analyst just ran.
  const signature = JSON.stringify([grid.id, excluded, includeLocked]);
  const report = checked?.signature === signature ? checked.report : null;

  const onError = (error: unknown) => {
    enqueueSnackbar(
      error instanceof Error
        ? error.message
        : intl.formatMessage({ id: "ERROR_OCCURRED" }),
      { variant: "error" }
    );
  };

  const check = useMutation(
    (nextOffset: number) =>
      fetchImpact(grid.id, {
        excluded_periods: excluded,
        include_locked: includeLocked,
        offset: nextOffset,
        limit: DETAIL_PAGE_SIZE,
      }),
    {
      onSuccess: (result) => setChecked({ signature, report: result }),
      onError,
    }
  );

  const apply = useMutation(
    () =>
      createApplication({
        grid: grid.id,
        include_locked: includeLocked,
        excluded_periods: excluded,
      }),
    {
      onSuccess: (application) => {
        if (application.status !== "done") {
          enqueueSnackbar(application.error, { variant: "error" });
          return;
        }
        enqueueSnackbar(
          intl.formatMessage(
            { id: "PRICING_TIER_APPLY_DONE" },
            { count: application.rows_written ?? 0 }
          ),
          { variant: "success" }
        );
        onClose();
      },
      onError,
    }
  );

  const addPeriod = () => {
    const [from, to] = draftPeriod;
    if (!from && !to) return;
    setExcluded([
      ...excluded,
      {
        date_from: from?.toISODate() ?? null,
        date_to: to?.toISODate() ?? null,
      },
    ]);
    setDraftPeriod([null, null]);
  };

  // Locked prices are stepped over unless the analyst says otherwise, so
  // they are not part of what this application would write.
  const writable =
    report === null
      ? 0
      : report.dates_off_grid - (includeLocked ? 0 : report.dates_locked);
  const readyToApply = report !== null && writable > 0;

  const page = (nextOffset: number) => {
    setOffset(nextOffset);
    check.mutate(nextOffset);
  };

  return (
    <ResponsiveDialog
      open
      maxWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "background.dialog",
          maxWidth: "calc(100vw - 64px)",
          minWidth: 640,
        },
      }}
      crossIcon
      onClose={onClose}
      dialogName="pricing-tier-apply"
      dialogId={grid.id}
      title={intl.formatMessage(
        { id: "PRICING_TIER_APPLY_TITLE" },
        { name: grid.name }
      )}
      actions={
        <>
          <AppButton intent="tonal" onClick={onClose} sx={SX_FORM_CANCEL}>
            <FormattedMessage id="CANCEL" />
          </AppButton>
          <AppButton
            disabled={!readyToApply}
            loading={apply.isLoading}
            onClick={() => apply.mutate()}
            sx={SX_FORM_SUBMIT}
          >
            <FormattedMessage id="PRICING_TIER_APPLY_NOW" />
          </AppButton>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <FormattedMessage id="PRICING_TIER_APPLY_HELP" />
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <Field label="PRICING_TIER_APPLY_EXCLUDE" width={360}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CustomDateRangePicker
                value={draftPeriod}
                onChange={(value) => setDraftPeriod(value)}
                hideLabels
                dense
              />
              <Button
                onClick={addPeriod}
                color="inherit"
                size="small"
                startIcon={<AddIcon />}
              >
                <FormattedMessage id="ADD" />
              </Button>
            </Box>
          </Field>
        </Box>

        {excluded.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {excluded.map((period, index) => (
              <Chip
                key={`${period.date_from}-${period.date_to}`}
                size="small"
                label={formatPeriod(
                  period,
                  intl.formatMessage({ id: "PRICING_TIER_APPLY_OPEN_END" })
                )}
                onDelete={() =>
                  setExcluded(excluded.filter((_, at) => at !== index))
                }
              />
            ))}
          </Box>
        )}

        <AppButton
          intent="secondary"
          startIcon={<ScienceOutlinedIcon />}
          onClick={() => page(0)}
          loading={check.isLoading}
          sx={{ alignSelf: "flex-start" }}
        >
          <FormattedMessage id="PRICING_TIER_CHECK_IMPACT" />
        </AppButton>

        <FormControlLabel
          control={
            <Switch
              checked={includeLocked}
              onChange={(event) => setIncludeLocked(event.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              <FormattedMessage id="PRICING_TIER_APPLY_INCLUDE_LOCKED" />
            </Typography>
          }
        />

        {report === null ? (
          <Alert severity="info">
            <FormattedMessage id="PRICING_TIER_APPLY_CHECK_FIRST" />
          </Alert>
        ) : writable === 0 ? (
          <Alert severity="success">
            <FormattedMessage id="PRICING_TIER_APPLY_NOTHING_TO_DO" />
          </Alert>
        ) : (
          <Alert severity="warning">
            <FormattedMessage
              id="PRICING_TIER_APPLY_WILL_WRITE"
              values={{ count: writable }}
            />
          </Alert>
        )}

        <ImpactSummary
          report={report}
          isLoading={check.isLoading && offset === 0}
          onPage={page}
          pageSize={DETAIL_PAGE_SIZE}
        />
      </Box>
    </ResponsiveDialog>
  );
};

export default ApplyDialog;
